import React, { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { makeStyles } from 'tss-react/mui'
import QRCode from 'qrcode'
import { useReactToPrint } from 'react-to-print'
import { Button, ButtonProps } from '@mui/material'

import posterBgReservation from 'ui/images/poster-bg-reservation.png'
import posterBgMedication from 'ui/images/poster-bg-medication.png'

export type PosterDesignNames = 'reservation' | 'medication'

const posters: {
  [key in PosterDesignNames]: {
    img: string
    fileName: string
  }
} = {
  reservation: {
    img: posterBgReservation,
    fileName: 'オンライン診療予約QRコードポスター',
  },
  medication: {
    img: posterBgMedication,
    fileName: 'オンライン服薬指導QRコードポスター',
  },
}

// NOTE: 各サイズは実際の印刷サイズで指定しており、いきなり印刷プレビューを表示させるため画面外に表示させている
const useStyles = makeStyles()(() => ({
  posterWrapper: {
    position: 'fixed',
    marginTop: 9999,
    marginRight: 9999,
  },
  poster: {
    display: 'flex',
    width: '210mm',
    height: '287mm',
    margin: 0,
    padding: 0,
  },
  textWrapper: {
    position: 'absolute',
    top: '121mm',
    left: '110mm',
    width: '75.5mm',
  },
  text: {
    fontWeight: 700,
    lineHeight: '18pt',
    fontSize: '15pt',
    textAlign: 'center',
    whiteSpace: 'pre-wrap',
  },
  qrCodeWrapper: {
    position: 'absolute',
    top: '150mm',
    left: '117mm',
  },
  qrCode: {
    width: '61.5mm',
    height: 'auto',
  },
}))

export type PosterPrintButtonProps = {
  design: PosterDesignNames
  text?: string
  url: string
}

const PosterPrintButton: React.FC<PosterPrintButtonProps & ButtonProps> = ({
  design,
  text,
  url,
  ...props
}: PosterPrintButtonProps & ButtonProps) => {
  const { t } = useTranslation()
  const { classes } = useStyles()
  const [qrCode, setQrCode] = useState<string>()

  // NOTE: 2行以内,1行につき14文字以内のテキストであることが前提で1行の時は空行を追加する
  const textLineCount = text?.split('\n').length
  const convertedText = textLineCount === 1 ? `\n${text}` : text

  const posterRef = useRef<HTMLDivElement | null>(null)
  const image = posters[design].img
  const originTile: string = document.title
  const fileName: string = posters[design].fileName

  useEffect(() => {
    QRCode.toDataURL(url)
      .then(setQrCode)
      .catch((err) => {
        console.error(err)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const printHandler = useReactToPrint({
    content: () => posterRef.current,
    onBeforeGetContent: () => {
      document.title = `${fileName}${format(new Date(), 'yyyy-MM-dd-HHmmss')}`
    },
    onAfterPrint: () => {
      document.title = originTile
    },
  })

  return image ? (
    <>
      <Button {...props} onClick={printHandler}>
        {t('印刷')}
      </Button>
      <div className={classes.posterWrapper}>
        <div
          ref={posterRef}
          className={classes.poster}
          style={{
            backgroundImage: `url(${image})`,
            backgroundSize: 'cover',
            position: 'relative',
          }}
        >
          <div className={classes.textWrapper}>
            <div className={classes.text}>{convertedText}</div>
          </div>
          <div className={classes.qrCodeWrapper}>
            <img
              className={classes.qrCode}
              src={qrCode}
              alt={t('qrコード画像')}
            />
          </div>
        </div>
      </div>
    </>
  ) : null
}

export default PosterPrintButton
